/**
 * Client-side image intake for Web Snap & Stash (FR-W4).
 *
 * Every decodable image is redrawn to a canvas (longest edge ≤ 2048px) and
 * re-encoded as JPEG q≈0.85 — bounding upload size and stripping EXIF. A
 * source the browser can't decode (typically HEIC outside Safari) is sent
 * as its original bytes when ≤ 8MB — the server's sharp intake handles it —
 * and rejected locally as payloadTooLarge above that, without a request.
 */
import type { ImageExtractionMimeType } from '@stash/shared-types';

export const MAX_EDGE_PX = 2048;
export const JPEG_QUALITY = 0.85;
export const MAX_FALLBACK_BYTES = 8 * 1024 * 1024;

const WIRE_MIME_TYPES: ReadonlySet<string> = new Set<ImageExtractionMimeType>([
  'image/jpeg', 'image/png', 'image/webp', 'image/heic',
]);

export interface NormalizedImage {
  imageData: string;
  mimeType: ImageExtractionMimeType;
  fileName: string;
}

export type NormalizeResult =
  | { ok: true; image: NormalizedImage }
  | { ok: false; error: 'payloadTooLarge' | 'unreadable' };

/** Canvas machinery, injectable for jsdom tests. */
export interface NormalizeDeps {
  /** Decode to dimensions (ImageBitmap-shaped); null when undecodable. */
  decode(file: File): Promise<{ width: number; height: number } | null>;
  /** Draw the decoded source at the target size and encode; null on failure. */
  encode(
    source: { width: number; height: number },
    targetWidth: number,
    targetHeight: number,
    quality: number
  ): Promise<Blob | null>;
}

const defaultDeps: NormalizeDeps = {
  async decode(file) {
    try {
      return await createImageBitmap(file);
    } catch {
      return null;
    }
  },
  async encode(source, targetWidth, targetHeight, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(source as ImageBitmap, 0, 0, targetWidth, targetHeight);
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  },
};

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function fallbackToOriginal(file: File): Promise<NormalizeResult> {
  if (file.size > MAX_FALLBACK_BYTES) return { ok: false, error: 'payloadTooLarge' };
  if (!WIRE_MIME_TYPES.has(file.type)) return { ok: false, error: 'unreadable' };
  return {
    ok: true,
    image: {
      imageData: await toBase64(file),
      mimeType: file.type as ImageExtractionMimeType,
      fileName: file.name || 'photo.jpg',
    },
  };
}

export async function normalizeImageFile(
  file: File,
  deps: NormalizeDeps = defaultDeps
): Promise<NormalizeResult> {
  const source = await deps.decode(file);
  if (!source) return fallbackToOriginal(file);

  const scale = Math.min(1, MAX_EDGE_PX / Math.max(source.width, source.height));
  const targetWidth = Math.round(source.width * scale);
  const targetHeight = Math.round(source.height * scale);

  const encoded = await deps.encode(source, targetWidth, targetHeight, JPEG_QUALITY);
  if (!encoded) return fallbackToOriginal(file);

  return {
    ok: true,
    image: {
      imageData: await toBase64(encoded),
      mimeType: 'image/jpeg',
      fileName: file.name || 'photo.jpg',
    },
  };
}
